import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useCatalogSkuSearch } from '../../hooks/useWmsSkus';
import { useDebounce } from '../../hooks/useDebounce';

interface BomItem {
  componentSkuId: string;
  qty: number;
  componentSku?: string;
  componentNameEn?: string;
}

interface SkuBomEditorProps {
  items: BomItem[];
  onChange: (items: BomItem[]) => void;
}

export default function SkuBomEditor({ items, onChange }: SkuBomEditorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);
  const searchResults = useCatalogSkuSearch(debouncedSearch);

  const handleAdd = (result: { id: string; sku: string; nameEn: string }) => {
    // Don't add duplicates
    if (items.some((item) => item.componentSkuId === result.id)) return;
    onChange([
      ...items,
      {
        componentSkuId: result.id,
        qty: 1,
        componentSku: result.sku,
        componentNameEn: result.nameEn,
      },
    ]);
    setSearchTerm('');
  };

  const handleRemove = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const handleQtyChange = (index: number, qty: number) => {
    const updated = [...items];
    updated[index] = { ...updated[index], qty: Math.max(1, qty) };
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <Label>Bill of Materials</Label>

      {items.length > 0 && (
        <div className="border rounded-lg divide-y">
          {items.map((item, index) => (
            <div key={item.componentSkuId} className="flex items-center gap-3 px-3 py-2">
              <div className="flex-1 min-w-0">
                <span className="font-mono text-sm">{item.componentSku}</span>
                {item.componentNameEn && (
                  <span className="text-muted-foreground text-sm ml-2">{item.componentNameEn}</span>
                )}
              </div>
              <Input
                type="number"
                min={1}
                value={item.qty}
                onChange={(e) => handleQtyChange(index, parseInt(e.target.value, 10) || 1)}
                className="w-20 h-8"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
                onClick={() => handleRemove(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="relative">
        <Input
          placeholder="Search SKU to add component..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="h-8"
        />
        {searchTerm.length >= 2 && searchResults.data && searchResults.data.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
            {searchResults.data.map((result) => (
              <button
                key={result.id}
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-accent text-sm flex items-center gap-2"
                onClick={() => handleAdd(result)}
              >
                <Plus className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                <span className="font-mono">{result.sku}</span>
                <span className="text-muted-foreground truncate">{result.nameEn}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
